import { AccountSelectorComponent } from 'src/app/shared/account-selector/account-selector.component';
import { AccountUserSelectorComponent } from 'src/app/shared/account-user-selector/account-user-selector.component';
import { ActiveToast } from 'ngx-toastr';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CountrySelectorComponent } from '../../shared/country-selector/country-selector.component';
import { CountryStateSelectorComponent, CountryStateValue } from '../../shared/country-state-selector/country-state-selector.component';
import { CreateAccountGQL, ValidationErrorFieldsFragment } from '../../graphql/gql';
import { CurrentUserService } from '../../services/current-user.service';
import { FluidModule } from 'primeng/fluid';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { RequiredMarkerComponent } from 'src/app/shared/required-marker/required-marker.component';
import { SelectModule } from 'primeng/select';
import { Subscription, filter, take } from 'rxjs';
import { ToastService } from "../../toast/toast.service";
import { AppValidationHelpers } from 'src/app/app-validation-helpers';

interface ParentAccount {
  clientNumber: number;
  company: string;
}

// How long the mutation may run before the user is told that the account is on its way.
const SLOW_SUBMIT_THRESHOLD_MS = 2500;

@Component({
  selector: 'app-create-account',
  imports: [
    AccountSelectorComponent,
    AccountUserSelectorComponent,
    ButtonModule,
    CardModule,
    CountrySelectorComponent,
    CountryStateSelectorComponent,
    FluidModule,
    InputTextModule,
    MessageModule,
    ReactiveFormsModule,
    RequiredMarkerComponent,
    SelectModule,
  ],
  templateUrl: './create-account.component.html',
  styleUrl: './create-account.component.css',
})
export class CreateAccountComponent implements OnInit, OnDestroy {
  @Input() success: () => void = () => {};

  // Lets the host dialog block closing while the mutation is in flight.
  @Output() submittingChange = new EventEmitter<boolean>();

  newAccountForm: FormGroup = this.formBuilder.group({
    parentClientNumber: [null, []],
    managerUserIds: [[], []],
    company: ['', [Validators.required, Validators.minLength(2), Validators.pattern('[a-zA-Z].*')]],
    street: ['', [Validators.required, Validators.minLength(2)]],
    city: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50), Validators.pattern('[a-zA-Z].*')]],
    zip: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(10)]],
    country: [null, [Validators.required]],
    countryStateComposite: [{ countryState: undefined, otherState: undefined }],
    phone: ['', Validators.compose([Validators.required, Validators.pattern(/^\+[0-9]+\.[0-9]+$/)])],
    fax: [null, [Validators.pattern('^[0-9\-\+\s\(\)\.]*$')]],
    email: ['', [Validators.required, Validators.email]],
    login: ['', [
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(255),
      Validators.pattern('[0-9a-z_\\-\\+@\\.]+')
    ]],
    title: ['' , []],
    firstname: ['', [Validators.required, Validators.minLength(2), Validators.pattern('[a-zA-Z].*')]],
    lastname: ['', [Validators.required, Validators.minLength(2), Validators.pattern('[a-zA-Z].*')]],
    cellPhone: ['', [Validators.pattern('^[0-9\-\+\s\(\)\.]*$')]],
  })

  // The account the current user belongs to. Used to default the parent selection, to make that
  // account selectable at all (the accounts query only returns its children), and to decide
  // whether the parent client number has to be sent to the mutation.
  currentAccount?: ParentAccount;

  submitting = false;

  private currentUserSubscription?: Subscription;
  private submitSubscription?: Subscription;
  private slowSubmitTimer?: ReturnType<typeof setTimeout>;
  private slowSubmitToast?: ActiveToast<any>;

  constructor(
    private formBuilder: FormBuilder,
    private createAccountGQL: CreateAccountGQL,
    private currentUserService: CurrentUserService,
    private toastService: ToastService,
  ) { }

  ngOnInit(): void {
    this.currentUserSubscription = this.currentUserService.currentUser.pipe(
      filter(userData => !!userData?.currentUser),
      take(1)
    ).subscribe(userData => {
      const user = userData!.currentUser!;
      const account = user.account;

      if (account?.clientNumber) {
        this.currentAccount = { clientNumber: account.clientNumber, company: account.company };
      }

      this.newAccountForm.patchValue({
        parentClientNumber: this.currentAccount?.clientNumber ?? null,
        // The creating user is granted management server-side regardless; pre-selecting them only
        // makes that visible.
        managerUserIds: [user.id]
      });
    });
  }

  ngOnDestroy(): void {
    this.currentUserSubscription?.unsubscribe();
    this.submitSubscription?.unsubscribe();
    this.clearSlowSubmitFeedback();

    // Going away mid-flight would otherwise leave the host dialog permanently unclosable.
    if (this.submitting) {
      this.submitting = false;
      this.submittingChange.emit(false);
    }
  }

  // The account whose users are offered for management: the chosen parent, or the current user's
  // own account when no parent is selected.
  get userSourceClientNumber(): number | null {
    return this.newAccountForm.get('parentClientNumber')?.value ?? this.currentAccount?.clientNumber ?? null;
  }

  submitForm() {
    if (this.submitting) {
      return;
    }

    this.newAccountForm.markAllAsTouched();

    if (this.newAccountForm.invalid) {
      return;
    }

    const formValue = this.newAccountForm.value;
    const composite: CountryStateValue = formValue.countryStateComposite
      ?? { countryState: undefined, otherState: undefined };

    this.startSubmitting();

    this.submitSubscription = this.createAccountGQL.mutate({
      ...formValue,
      parentClientNumber: this.parentClientNumberForMutation(formValue.parentClientNumber),
      countryState: composite.countryState,
      otherState: composite.otherState,
      countryStateComposite: undefined
    }).subscribe({
      next: (result) => {
        this.stopSubmitting();

        if (result.data === null || result.data === undefined) {
          this.toastService.show('error', $localize`Failed to create account.`);
          return;
        }

        const creationResult = result.data.createAccount;

        switch (creationResult.__typename) {
          case "Account":
            this.toastService.show('success', $localize`:create account form|Account created toast:New account created`);
            this.success();
            break;

          case "ValidationErrors":
            this.reportValidationErrors(creationResult.validationMessages);
            break;
        }
      },
      error: () => {
        this.stopSubmitting();
        this.toastService.show('error', $localize`Failed to create account.`);
      }
    });
  }

  /**
   * Shows the server's validation errors.
   *
   * Anything which could not be attached to a form control is shown as a toast instead, because
   * there is no field for the user to look at — without this the submission fails silently. The
   * toast is an error rather than an info one so it stays on screen until dismissed.
   * See BNDDEV-10062.
   *
   * @param {ValidationErrorFieldsFragment[]} validationMessages - The errors returned by the mutation.
   */
  private reportValidationErrors(validationMessages: ValidationErrorFieldsFragment[]) {
    const unmapped = AppValidationHelpers.applyValidationErrors(validationMessages, this.newAccountForm);
    const unmappedMessage = AppValidationHelpers.unmappedErrorMessage(unmapped);

    if (unmappedMessage === null) {
      this.toastService.show('info', $localize`Please correct the validation errors and try again.`);
      return;
    }

    this.toastService.show('error', unmappedMessage);
  }

  // `createAccount` treats a missing parent as the current account, so selecting one's own account
  // and clearing the field are the same request. Collapsing both to null keeps the payload
  // canonical instead of sending a parent the backend would have defaulted to anyway.
  private parentClientNumberForMutation(parentClientNumber: number | null): number | null {
    if (parentClientNumber === null || parentClientNumber === this.currentAccount?.clientNumber) {
      return null;
    }

    return parentClientNumber;
  }

  private startSubmitting() {
    this.submitting = true;
    this.submittingChange.emit(true);

    // Greying the fields out is not enough on its own: without this the fields stay focusable and
    // editable by keyboard while the mutation is in flight. `emitEvent: false` keeps the
    // disable/enable round trip from looking like a user edit, which would clear the remote
    // validation errors `applyValidationErrors` installs.
    this.newAccountForm.disable({ emitEvent: false });

    this.slowSubmitTimer = setTimeout(() => {
      this.slowSubmitTimer = undefined;
      this.slowSubmitToast = this.toastService.show(
        'info',
        $localize`:create account form|Slow creation toast:Account being created. It should be visible very shortly`,
        undefined,
        { disableTimeOut: true }
      );
    }, SLOW_SUBMIT_THRESHOLD_MS);
  }

  private stopSubmitting() {
    this.clearSlowSubmitFeedback();
    this.newAccountForm.enable({ emitEvent: false });
    this.submitting = false;
    this.submittingChange.emit(false);
  }

  private clearSlowSubmitFeedback() {
    if (this.slowSubmitTimer !== undefined) {
      clearTimeout(this.slowSubmitTimer);
      this.slowSubmitTimer = undefined;
    }

    this.toastService.hide(this.slowSubmitToast);
    this.slowSubmitToast = undefined;
  }

  getRemoteValidationMessages = AppValidationHelpers.getRemoteValidationMessages
}
